import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { useAuth } from '../../context/AuthContext';
import { Plus, Car, Gauge } from 'lucide-react';
import toast from 'react-hot-toast';

const FINANCE_ROLES = ['Super Admin', 'Secretary', 'Director'];
const emptyCreateForm = { code: '', registration_number: '', make_model: '', cost_center_id: '', acquisition_date: '', acquisition_cost: '', salvage_value: '0', useful_life_years: '' };
const emptyPeriodForm = { period_start: '', period_end: '' };

const DepreciationModal = ({ vehicle, allVehicles, onClose, onDone }) => {
  const [form, setForm] = useState(emptyPeriodForm);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResults(null);
    try {
      if (vehicle) {
        const res = await accountsApi.post(`/vehicles/${vehicle.id}/depreciate`, form);
        toast.success(`Depreciation of ${Number(res.data.run.amount).toFixed(2)} posted for ${vehicle.code}`);
        onDone();
        onClose();
      } else {
        const res = await accountsApi.post('/vehicles/depreciate-all', form);
        setResults(res.data.outcomes);
        const successCount = res.data.outcomes.filter(o => o.ok).length;
        if (successCount > 0) {
          toast.success(`Depreciation posted for ${successCount} vehicle(s)`);
          onDone();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to run depreciation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Gauge size={18} /> Run Depreciation{vehicle ? ` — ${vehicle.code}` : ` — All Active Vehicles (${allVehicles.length})`}
          </h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period Start</label>
              <input required type="date" value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period End</label>
              <input required type="date" value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Straight-line, prorated by days in this period, and capped so a vehicle never depreciates past its salvage value.
            Must start on or after each vehicle's acquisition date or last depreciated-through date.
          </p>

          {results && (
            <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} className={`border-t border-gray-100 ${r.ok ? '' : 'bg-red-50'}`}>
                      <td className="px-3 py-1.5 text-gray-700">{r.vehicle_code}</td>
                      <td className={`px-3 py-1.5 text-right ${r.ok ? 'text-green-700' : 'text-red-600'}`}>
                        {r.ok ? Number(r.amount).toFixed(2) : r.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
            <button type="submit" disabled={submitting}
              className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
              {submitting ? 'Posting...' : 'Run Depreciation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Vehicles = () => {
  const { user } = useAuth();
  const isFinance = FINANCE_ROLES.includes(user?.role);

  const [vehicles, setVehicles] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [submitting, setSubmitting] = useState(false);
  const [depreciatingVehicle, setDepreciatingVehicle] = useState(null);
  const [showDepreciateAll, setShowDepreciateAll] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [vehiclesRes, ccRes] = await Promise.all([
        accountsApi.get('/vehicles'),
        accountsApi.get('/cost-centers', { params: { is_active: true } }),
      ]);
      setVehicles(vehiclesRes.data.vehicles);
      setCostCenters(ccRes.data.cost_centers);
    } catch (err) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/vehicles', {
        code: form.code, registration_number: form.registration_number || null, make_model: form.make_model || null,
        cost_center_id: form.cost_center_id || null, acquisition_date: form.acquisition_date,
        acquisition_cost: Number(form.acquisition_cost), salvage_value: Number(form.salvage_value) || 0,
        useful_life_years: Number(form.useful_life_years),
      });
      toast.success('Vehicle created');
      setShowCreateModal(false);
      setForm(emptyCreateForm);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const activeVehicles = vehicles.filter(v => v.is_active);

  return (
    <Layout title="Vehicles">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500 max-w-2xl">
          The fixed-asset register for vehicles — acquisition cost, straight-line depreciation, and Repair &amp; POL costs attributed per vehicle.
        </p>
        {isFinance && (
          <div className="flex gap-2 shrink-0 ml-4">
            <button onClick={() => setShowDepreciateAll(true)}
              className="flex items-center gap-2 border border-blue-800 text-blue-800 px-4 py-2 rounded-lg text-sm hover:bg-blue-50">
              <Gauge size={16} /> Run Depreciation for All
            </button>
            <button onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
              <Plus size={16} /> New Vehicle
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Registration</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Make/Model</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Acquisition Cost</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Accum. Depreciation</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Net Book Value</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Total Repair</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Total POL</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
              {isFinance && <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isFinance ? 10 : 9} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : vehicles.length === 0 ? (
              <tr><td colSpan={isFinance ? 10 : 9} className="text-center py-8 text-gray-500">No vehicles yet</td></tr>
            ) : vehicles.map(v => (
              <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{v.code}</td>
                <td className="px-4 py-3 text-gray-700">{v.registration_number || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{v.make_model || '—'}</td>
                <td className="px-4 py-3 text-right text-gray-800">{Number(v.acquisition_cost).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{Number(v.accumulated_depreciation).toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-800">{Number(v.net_book_value).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{Number(v.total_repair_cost).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{Number(v.total_pol_cost).toFixed(2)}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${v.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {v.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {isFinance && (
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => setDepreciatingVehicle(v)} className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1">
                      <Gauge size={14} /> Depreciate
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-bold flex items-center gap-2"><Car size={18} /> New Vehicle</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3 overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                <input required type="text" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. VEH-001"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Registration Number</label>
                  <input type="text" value={form.registration_number}
                    onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Make / Model</label>
                  <input type="text" value={form.make_model}
                    onChange={(e) => setForm({ ...form, make_model: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center (optional)</label>
                <select value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">None</option>
                  {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Acquisition Date</label>
                <input required type="date" value={form.acquisition_date}
                  onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Acquisition Cost</label>
                  <input required type="number" step="0.01" min="0.01" value={form.acquisition_cost}
                    onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Salvage Value</label>
                  <input type="number" step="0.01" min="0" value={form.salvage_value}
                    onChange={(e) => setForm({ ...form, salvage_value: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Useful Life (yrs)</label>
                  <input required type="number" step="1" min="1" value={form.useful_life_years}
                    onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {depreciatingVehicle && (
        <DepreciationModal vehicle={depreciatingVehicle} allVehicles={activeVehicles}
          onClose={() => setDepreciatingVehicle(null)} onDone={fetchAll} />
      )}
      {showDepreciateAll && (
        <DepreciationModal vehicle={null} allVehicles={activeVehicles}
          onClose={() => setShowDepreciateAll(false)} onDone={fetchAll} />
      )}
    </Layout>
  );
};

export default Vehicles;
